import { inject, Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { map } from 'rxjs/operators';
import { DynamicForm, DynamicFormInput, DynamicFieldInput, DynamicField, UserInfo, UserInfoInput, Case, CasesConnection, UsersConnection, DynamicFormsConnection, CaseExportHistoriesConnection, CaseExportHistory, CaseExportHistoryEdge, Country, CountriesConnection, CountryInput, ContentTemplate, ContentTemplatesConnection, CreateContentTemplateInput, UpdateContentTemplateInput } from './graphql.types';

const FIELD_FRAGMENT = `
  fragment FieldParts on DynamicField {
    id
    dynamicFormId
    fieldId
    fieldName
    fieldType
    modelKey
    group
    col
    row
    span
    index
    options
    isForExtraction
    isActive
    isRequired
    remarks
    createdAt
    createdBy
    updatedAt
    updatedBy
  }
`;

const FORM_FRAGMENT = `
  fragment FormParts on DynamicForm {
    id
    formName
    description
    status
    remarks
    createdAt
    createdBy
    updatedAt
    updatedBy
    fields {
      ...FieldParts
    }
  }
  ${FIELD_FRAGMENT}
`;

const GET_DYNAMIC_FORMS = gql`
  query getDynForms($first: Int, $after: String) {
    dynamicForms(first: $first, after: $after, order: { createdAt: ASC }) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      nodes {
        ...FormParts
      }
    }
  }
  ${FORM_FRAGMENT}
`;

const GET_DYNAMIC_FORM = gql`
  query getDynFormById($id: UUID!) {
    dynamicFormById(id: $id) {
      ...FormParts
    }
  }
  ${FORM_FRAGMENT}
`;

const CREATE_DYNAMIC_FORM = gql`
  mutation createDynamicForm(
    $formName: String!
    $description: String
    $status: String!
    $remarks: String
  ) {
    createDynamicForm(
      formName: $formName,
      description: $description,
      status: $status,
      remarks: $remarks
    ) {
      ...FormParts
    }
  }
  ${FORM_FRAGMENT}
`;

const UPDATE_DYNAMIC_FORM = gql`
  mutation updateDynamicForm(
    $id: UUID!
    $formName: String!
    $description: String
    $status: String!
    $remarks: String
  ) {
    updateDynamicForm(id: $id,
      formName: $formName,
      description: $description,
      status: $status,
      remarks: $remarks
    ) {
      ...FormParts
    }
  }
  ${FORM_FRAGMENT}
`;

const DELETE_DYNAMIC_FORM = gql`
  mutation deleteDynamicForm($id: UUID!) {
    deleteDynamicForm(id: $id) {
      id
    }
  }
`;

const CREATE_FIELD = gql`
  mutation createDynamicField(
    $dynamicFormId: UUID!
    $fieldId: String
    $fieldName: String!
    $fieldType: FieldType!
    $modelKey: String!
    $group: String
    $index: Int!
    $col: Int!
    $row: Int!
    $span: Int!
    $options: [String!]!
    $isForExtraction: Boolean!
    $isRequired: Boolean!
    $isActive: Boolean!
    $remarks: String
  ) {
    createDynamicField(
      dynamicFormId: $dynamicFormId,
      fieldId: $fieldId,
      fieldName: $fieldName,
      fieldType: $fieldType,
      modelKey: $modelKey,
      group: $group,
      index: $index,
      col: $col,
      row: $row,
      span: $span,
      options: $options,
      isForExtraction: $isForExtraction,
      isRequired: $isRequired,
      isActive: $isActive,
      remarks: $remarks
    ) {
      ...FieldParts
    }
  }
  ${FIELD_FRAGMENT}
`;

const UPDATE_FIELD = gql`
  mutation updateDynamicField(
    $id: UUID!
    $dynamicFormId: UUID!
    $fieldId: String
    $fieldName: String!
    $fieldType: FieldType!
    $modelKey: String!
    $group: String
    $index: Int!
    $col: Int!
    $row: Int!
    $span: Int!
    $options: [String!]!
    $isForExtraction: Boolean!
    $isRequired: Boolean!
    $isActive: Boolean!
    $remarks: String
  ) {
    updateDynamicField(id: $id,
      dynamicFormId: $dynamicFormId,
      fieldId: $fieldId,
      fieldName: $fieldName,
      fieldType: $fieldType,
      modelKey: $modelKey,
      group: $group,
      index: $index,
      col: $col,
      row: $row,
      span: $span,
      options: $options,
      isForExtraction: $isForExtraction,
      isRequired: $isRequired,
      isActive: $isActive,
      remarks: $remarks
    ) {
      ...FieldParts
    }
  }
  ${FIELD_FRAGMENT}
`;

const DELETE_FIELD = gql`
  mutation deleteDynamicField($id: UUID!) {
    deleteDynamicField(id: $id)
  }
`;

const USER_FRAGMENT = `
  fragment UserInfoParts on UserInfo {
    id
    username
    bmsId
    fullName
    email
    role
    isActive
    lastLogin
    createdAt
    createdBy
    updatedAt
    updatedBy
  }
`;

const GET_USERS = gql`
  query getUsers($first: Int, $after: String, $where: UserInfoFilterInput) {
    userInfos(first: $first, after: $after, where: $where, order: { lastLogin: DESC, fullName: ASC }) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      nodes {
        ...UserInfoParts
      }
    }
  }
  ${USER_FRAGMENT}
`;

const CREATE_USER = gql`
  mutation createUserInfo(
    $username: String!
    $bmsId: String
    $fullName: String!
    $email: String!
    $role: String
    $isActive: Boolean!
  ) {
    createUserInfo(
      username: $username
      bmsId: $bmsId
      fullName: $fullName
      email: $email
      role: $role
      isActive: $isActive
    ) {
      ...UserInfoParts
    }
  }
  ${USER_FRAGMENT}
`;

const UPDATE_USER = gql`
  mutation updateUserInfo(
    $id: UUID!
    $username: String!
    $bmsId: String
    $fullName: String!
    $email: String!
    $role: String
    $isActive: Boolean!
  ) {
    updateUserInfo(
      id: $id
      username: $username
      bmsId: $bmsId
      fullName: $fullName
      email: $email
      role: $role
      isActive: $isActive
    ) {
      ...UserInfoParts
    }
  }
  ${USER_FRAGMENT}
`;

const DELETE_USER = gql`
  mutation deleteUserInfo($id: UUID!) {
    deleteUserInfo(id: $id) {
      id
    }
  }
`;

const GET_USER_BY_USERNAME = gql`
  query getUserByUsername($username: String!) {
    userInfoByUsername(username: $username) {
      ...UserInfoParts
    }
  }
  ${USER_FRAGMENT}
`;

const VERIFY_USER = gql`
  query verifyActiveUserByUsername($username: String!) {
    verifyActiveUserByUsername(username: $username) {
      ...UserInfoParts
    }
  }
  ${USER_FRAGMENT}
`

const CASE_DETAIL_FRAGMENT = `
  fragment CaseDetailParts on CaseDetail {
    caseId
    createdAt
    createdBy
    fieldId
    fieldName
    id
    updatedAt
    updatedBy
    value
  }
`;

const CASE_FRAGMENT = `
  fragment CaseParts on Case {
    agentEmail
    agentId
    agentName
    completedOn
    createdAt
    createdBy
    dynamicFormId
    id
    startedOn
    updatedAt
    updatedBy
    details {
      ...CaseDetailParts
    }
    dynamicForm {
      formName
    }
  }
  ${CASE_DETAIL_FRAGMENT}
`;

const GET_CASES = gql`
  query getAllCases($first: Int, $after: String, $where: CaseFilterInput) {
    cases(first: $first, after: $after, where: $where, order: { createdAt: DESC }) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      nodes {
        ...CaseParts
      }
    }
  }
  ${CASE_FRAGMENT}
`;

const GET_CASE = gql`
  query getCaseById($id: UUID!) {
    caseById(id: $id) {
      ...CaseParts
    }
  }
  ${CASE_FRAGMENT}
`;

const GET_CASE_EXPORT_HISTORIES = gql`
  query getCaseHistories($first: Int!, $after: String) {
    caseExportHistories(after: $after, first: $first, order: { createdAt: DESC }) {
      totalCount
      edges {
        cursor
        node {
          createdAt
          createdBy
          downloadedAt
          downloadedBy
          fileName
          filePath
          fromDate
          id
          isDownloaded
          toDate
          updatedAt
          updatedBy
        }
      }
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
    }
  }
`;

const GET_CASE_EXPORT_HISTORY_BY_DATE_RANGE = gql`
  query getCaseExportHistoryByDateRange($where: CaseExportHistoryFilterInput) {
    caseExportHistories(first: 1, order: { createdAt: DESC }, where: $where) {
      edges {
        node {
          id
          fileName
          filePath
          fromDate
          toDate
          isDownloaded
        }
      }
    }
  }
`;

const GET_COUNTRY = gql`
  query CountryQueryById($id: UUID!) {
    countryById(countryId: $id) {
      id
      countryCode
      countryName
      region
      language
      createdAt
      updatedAt
      createdBy
      updatedBy
    }
  }
`;

const CREATE_COUNTRY = gql`
  mutation createCountry(
    $countryCode: String!
    $countryName: String!
    $region: String
    $language: String
  ) {
    createCountry(
      countryCode: $countryCode
      countryName: $countryName
      region: $region
      language: $language
    ) {
      id
      countryCode
      countryName
      region
      language
      createdAt
      updatedAt
      createdBy
      updatedBy
    }
  }
`;

const UPDATE_COUNTRY = gql`
  mutation updateCountry(
    $id: UUID!
    $countryName: String!
    $region: String
    $language: String
  ) {
    updateCountry(
      id: $id
      input: {
        countryName: $countryName,
        language: $language,
        region: $region }
    ) {
      id
      countryCode
    }
  }
`;

const GET_COUNTRIES = gql`
  query CountryQuery($first: Int, $after: String, $where: CountryFilterInput) {
    countries(first: $first, after: $after, where: $where, order: { countryName: ASC }) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      nodes {
        id
        countryCode
        countryName
        region
        language
        createdAt
        updatedAt
        createdBy
        updatedBy
      }
    }
  }
`;

const CONTENT_TEMPLATE_TRANSLATION_FRAGMENT = `
  fragment ContentTemplateTranslationParts on ContentTemplateTranslation {
    id
    contentTemplateId
    languageCode
    subject
    body
    createdAt
    createdBy
    updatedAt
    updatedBy
  }
`;

const CONTENT_TEMPLATE_FRAGMENT = `
  fragment ContentTemplateParts on ContentTemplate {
    id
    templateName
    templateType
    isActive
    createdAt
    createdBy
    updatedAt
    updatedBy
  }
`;

const GET_CONTENT_TEMPLATES = gql`
  query getContentTemplates($first: Int, $after: String, $where: ContentTemplateFilterInput) {
    contentTemplates(first: $first, after: $after, where: $where, order: { templateName: ASC }) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      nodes {
        ...ContentTemplateParts
        translations {
          id
        }
      }
    }
  }
  ${CONTENT_TEMPLATE_FRAGMENT}
`;

const GET_CONTENT_TEMPLATE = gql`
  query getContentTemplateById($id: UUID!) {
    contentTemplateById(id: $id) {
      ...ContentTemplateParts
      translations {
        ...ContentTemplateTranslationParts
      }
    }
  }
  ${CONTENT_TEMPLATE_FRAGMENT}
  ${CONTENT_TEMPLATE_TRANSLATION_FRAGMENT}
`;

const CREATE_CONTENT_TEMPLATE = gql`
  mutation createContentTemplate($input: CreateContentTemplateInput!) {
    createContentTemplate(input: $input) {
      ...ContentTemplateParts
      translations {
        ...ContentTemplateTranslationParts
      }
    }
  }
  ${CONTENT_TEMPLATE_FRAGMENT}
  ${CONTENT_TEMPLATE_TRANSLATION_FRAGMENT}
`;

const UPDATE_CONTENT_TEMPLATE = gql`
  mutation updateContentTemplate($id: UUID!, $input: UpdateContentTemplateInput!) {
    updateContentTemplate(id: $id, input: $input) {
      ...ContentTemplateParts
      translations {
        ...ContentTemplateTranslationParts
      }
    }
  }
  ${CONTENT_TEMPLATE_FRAGMENT}
  ${CONTENT_TEMPLATE_TRANSLATION_FRAGMENT}
`;

const DELETE_CONTENT_TEMPLATE = gql`
  mutation deleteContentTemplate($id: UUID!) {
    deleteContentTemplate(id: $id)
  }
`;

const DELETE_CONTENT_TEMPLATE_TRANSLATION = gql`
  mutation deleteContentTemplateTranslation($id: UUID!) {
    deleteContentTemplateTranslation(id: $id)
  }
`;

@Injectable({ providedIn: 'root' })
export class GraphqlService {
  private apollo = inject(Apollo);

  getDynamicForms(first: number = 20, after: string | null = null) {
    return this.apollo
      .query<{ dynamicForms: DynamicFormsConnection }>({
        query: GET_DYNAMIC_FORMS,
        variables: { first, after },
        fetchPolicy: 'cache-first',
      })
      .pipe(map(r => r.data!.dynamicForms));
  }

  getDynamicForm(id: string) {
    return this.apollo
      .query<{ dynamicFormById: DynamicForm }>({
        query: GET_DYNAMIC_FORM,
        variables: { id },
        fetchPolicy: 'cache-first',
      })
      .pipe(map(r => r.data?.dynamicFormById as DynamicForm));
  }

  createDynamicForm(input: DynamicFormInput) {
    return this.apollo
      .mutate<{ createDynamicForm: DynamicForm }>({
        mutation: CREATE_DYNAMIC_FORM,
        variables: {
          formName: input.formName,
          description: input.description,
          status: input.status,
          remarks: input.remarks,
        },
      })
      .pipe(map(r => r.data?.createDynamicForm as DynamicForm));
  }

  updateDynamicForm(id: string, input: DynamicFormInput) {
    return this.apollo
      .mutate<{ updateDynamicForm: DynamicForm }>({
        mutation: UPDATE_DYNAMIC_FORM,
        variables: {
          id,
          formName: input.formName,
          description: input.description,
          status: input.status,
          remarks: input.remarks,
        },
      })
      .pipe(map(r => r.data?.updateDynamicForm as DynamicForm));
  }

  deleteDynamicForm(id: string) {
    return this.apollo.mutate({
      mutation: DELETE_DYNAMIC_FORM,
      variables: { id },
      refetchQueries: [{ query: GET_DYNAMIC_FORMS }],
    });
  }

  createField(input: DynamicFieldInput) {
    return this.apollo
      .mutate<{ createDynamicField: DynamicField }>({
        mutation: CREATE_FIELD,
        variables: {
          dynamicFormId: input.dynamicFormId,
          fieldId: input.fieldId ?? null,
          fieldName: input.fieldName,
          fieldType: input.fieldType,
          modelKey: input.modelKey,
          group: input.group,
          index: input.index,
          col: input.col,
          row: input.row,
          span: input.span,
          options: input.options,
          isForExtraction: input.isForExtraction,
          isRequired: input.isRequired,
          isActive: input.isActive,
          remarks: input.remarks,
        },
      })
      .pipe(map(r => r.data?.createDynamicField as DynamicField));
  }

  updateField(id: string, input: DynamicFieldInput) {
    return this.apollo
      .mutate<{ updateDynamicField: DynamicField }>({
        mutation: UPDATE_FIELD,
        variables: {
          id,
          dynamicFormId: input.dynamicFormId,
          fieldId: input.fieldId,
          fieldName: input.fieldName,
          fieldType: input.fieldType,
          modelKey: input.modelKey,
          group: input.group,
          index: input.index,
          col: input.col,
          row: input.row,
          span: input.span,
          options: input.options,
          isForExtraction: input.isForExtraction,
          isRequired: input.isRequired,
          isActive: input.isActive,
          remarks: input.remarks,
        },
      })
      .pipe(map(r => r.data?.updateDynamicField as DynamicField));
  }

  deleteField(id: string) {
    return this.apollo.mutate({
      mutation: DELETE_FIELD,
      variables: { id },
    });
  }

  getUsers(first: number = 20, after: string | null = null, searchTerm: string = '', role: string = '') {
    const trimmed = searchTerm.trim();
    const conditions: object[] = [];

    if (trimmed) {
      conditions.push({
        or: [
          { fullName: { contains: trimmed } },
          { username: { contains: trimmed } },
          { bmsId: { contains: trimmed } },
          { email: { contains: trimmed } },
        ],
      });
    }

    if (role) {
      conditions.push({ role: { eq: role } });
    }

    const where = conditions.length === 1
      ? conditions[0]
      : conditions.length > 1
        ? { and: conditions }
        : null;

    return this.apollo
      .query<{ userInfos: UsersConnection }>({
        query: GET_USERS,
        variables: { first, after, where },
        fetchPolicy: 'cache-first',
      })
      .pipe(map(r => r.data!.userInfos));
  }

  createUser(input: UserInfoInput) {
    return this.apollo
      .mutate<{ createUserInfo: UserInfo }>({
        mutation: CREATE_USER,
        variables: input,
      })
      .pipe(map(r => r.data?.createUserInfo as UserInfo));
  }

  updateUser(id: string, input: UserInfoInput) {
    return this.apollo
      .mutate<{ updateUserInfo: UserInfo }>({
        mutation: UPDATE_USER,
        variables: { id, ...input },
      })
      .pipe(map(r => r.data?.updateUserInfo as UserInfo));
  }

  deleteUser(id: string) {
    return this.apollo.mutate({
      mutation: DELETE_USER,
      variables: { id },
    });
  }

  getUserByUsername(username: string) {
    return this.apollo
      .query<{ userInfoByUsername: UserInfo | null }>({
        query: GET_USER_BY_USERNAME,
        variables: { username },
        fetchPolicy: 'cache-first',
      })
      .pipe(map(r => r.data?.userInfoByUsername ?? null));
  }

  verifyUser(username: string) {
    return this.apollo
      .query<{ verifyActiveUserByUsername: UserInfo | null }>({
        query: VERIFY_USER,
        variables: { username },
        fetchPolicy: 'network-only',
      })
      .pipe(map(r => r.data?.verifyActiveUserByUsername ?? null));
  }

  getCases(first: number = 20, after: string | null = null, searchTerm: string = '', caseId: string = '') {
    const trimmed = searchTerm.trim();
    const caseIdTrimmed = caseId.trim();
    const conditions: object[] = [];

    if (trimmed) {
      conditions.push({
        or: [
          { agentName: { contains: trimmed } },
          { agentEmail: { contains: trimmed } },
        ],
      });
    }

    if (caseIdTrimmed) {
      conditions.push({ id: { eq: caseIdTrimmed } });
    }

    const where = conditions.length === 1
      ? conditions[0]
      : conditions.length > 1
        ? { and: conditions }
        : null;

    return this.apollo
      .query<{ cases: CasesConnection }>({
        query: GET_CASES,
        variables: { first, after, where },
        fetchPolicy: 'cache-first',
      })
      .pipe(map(r => r.data!.cases));
  }

  getCase(id: string) {
    return this.apollo
      .query<{ caseById: Case }>({
        query: GET_CASE,
        variables: { id },
        fetchPolicy: 'cache-first',
      })
      .pipe(map(r => r.data?.caseById as Case));
  }

  getCaseExportHistories(first: number = 20, after: string | null = null) {
    return this.apollo
      .query<{ caseExportHistories: CaseExportHistoriesConnection }>({
        query: GET_CASE_EXPORT_HISTORIES,
        variables: { first, after },
        fetchPolicy: 'cache-first',
      })
      .pipe(map(r => r.data!.caseExportHistories));
  }

  getCountries(first: number = 20, after: string | null = null, searchTerm: string = '') {
    const trimmed = searchTerm.trim();
    const where = trimmed
      ? {
          or: [
            { countryName: { contains: trimmed } },
            { countryCode: { contains: trimmed } },
            { region: { contains: trimmed } },
          ],
        }
      : null;

    return this.apollo
      .query<{ countries: CountriesConnection }>({
        query: GET_COUNTRIES,
        variables: { first, after, where },
        fetchPolicy: 'cache-first',
      })
      .pipe(map(r => r.data!.countries));
  }

  getCountry(id: string) {
    return this.apollo
      .query<{ countryById: Country }>({
        query: GET_COUNTRY,
        variables: { id },
        fetchPolicy: 'cache-first',
      })
      .pipe(map(r => r.data?.countryById as Country));
  }

  createCountry(input: CountryInput) {
    return this.apollo
      .mutate<{ createCountry: Country }>({
        mutation: CREATE_COUNTRY,
        variables: input,
      })
      .pipe(map(r => r.data?.createCountry as Country));
  }

  updateCountry(id: string, input: CountryInput) {
    return this.apollo
      .mutate<{ updateCountry: Country }>({
        mutation: UPDATE_COUNTRY,
        variables: { id, ...input },
      })
      .pipe(map(r => r.data?.updateCountry as Country));
  }

  getContentTemplates(first: number = 20, after: string | null = null, searchTerm: string = '') {
    const trimmed = searchTerm.trim();
    const where = trimmed ? { templateName: { contains: trimmed } } : null;
    return this.apollo
      .query<{ contentTemplates: ContentTemplatesConnection }>({
        query: GET_CONTENT_TEMPLATES,
        variables: { first, after, where },
        fetchPolicy: 'cache-first',
      })
      .pipe(map(r => r.data!.contentTemplates));
  }

  getContentTemplate(id: string) {
    return this.apollo
      .query<{ contentTemplateById: ContentTemplate }>({
        query: GET_CONTENT_TEMPLATE,
        variables: { id },
        fetchPolicy: 'cache-first',
      })
      .pipe(map(r => r.data?.contentTemplateById as ContentTemplate));
  }

  createContentTemplate(input: CreateContentTemplateInput) {
    return this.apollo
      .mutate<{ createContentTemplate: ContentTemplate }>({
        mutation: CREATE_CONTENT_TEMPLATE,
        variables: { input },
      })
      .pipe(map(r => {
        const result = r.data?.createContentTemplate;
        if (!result) throw new Error('Failed to create content template');
        return result;
      }));
  }

  updateContentTemplate(id: string, input: UpdateContentTemplateInput) {
    return this.apollo
      .mutate<{ updateContentTemplate: ContentTemplate }>({
        mutation: UPDATE_CONTENT_TEMPLATE,
        variables: { id, input },
      })
      .pipe(map(r => {
        const result = r.data?.updateContentTemplate;
        if (!result) throw new Error('Failed to update content template');
        return result;
      }));
  }

  deleteContentTemplate(id: string) {
    return this.apollo.mutate<{ deleteContentTemplate: boolean }>({
      mutation: DELETE_CONTENT_TEMPLATE,
      variables: { id },
    });
  }

  deleteContentTemplateTranslation(id: string) {
    return this.apollo.mutate<{ deleteContentTemplateTranslation: boolean }>({
      mutation: DELETE_CONTENT_TEMPLATE_TRANSLATION,
      variables: { id },
    });
  }

  getDistinctCountryLanguages() {
    return this.apollo
      .query<{ countries: CountriesConnection }>({
        query: GET_COUNTRIES,
        variables: { first: 300, after: null, where: null },
        fetchPolicy: 'cache-first',
      })
      .pipe(
        map(r => {
          const langs = r.data!.countries.nodes
            .map(c => c.language)
            .filter((l): l is string => !!l && l.trim() !== '');
          return [...new Set(langs)].sort();
        })
      );
  }

  getCaseExportHistoryByDateRange(fromDate: Date | null, toDate: Date | null) {
    const conditions: object[] = [];
    if (fromDate) conditions.push({ fromDate: { eq: fromDate } });
    if (toDate) conditions.push({ toDate: { eq: toDate } });

    const where = conditions.length === 1
      ? conditions[0]
      : conditions.length > 1
        ? { and: conditions }
        : null;

    return this.apollo
      .query<{ caseExportHistories: { edges: CaseExportHistoryEdge[] } }>({
        query: GET_CASE_EXPORT_HISTORY_BY_DATE_RANGE,
        variables: { where },
        fetchPolicy: 'cache-first',
      })
      .pipe(map(r => r.data?.caseExportHistories.edges[0]?.node ?? null));
  }
}
